import React, { useState, useEffect, useContext } from "react";
import {
  View,
  FlatList,
  TouchableOpacity,
  ScrollView,
  Platform} from "react-native";
import AntDesign from "@expo/vector-icons/AntDesign";
import { ApiService, setAuthToken } from "../../service/ApiService";
import SkeletonLoader from "../../components/SkeletonLoader";
import commonStyles from "../../styles/commonStyles";

import globalEventEmitter from "../../components/eventEmitter";

import PropertiesStyle from "../Properties/PropertiesStyle";
import CommonDropdown from "../../components/Dropdown";
import { getUserIdToUse } from "../../utils/getUserIdToUse";
import Constants from "expo-constants";
import {
  ShareholderTransactionListModel,
  ShareholderNameAPIOptionModel,
  SelectedShareholderNameAPIOptions} from "../../types/IPayment";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  formatDate,
  inferShareholderTransactionType} from "../../common/utils";
import {
  InvestmentModel} from "../../types/IInvestmentTypes";
import {
  QuarterStaticData,
  YearStaticData} from "../../static/SharedStaticsData";
import {
  aggregateShareholderOptions,
  buildShareholderOptions,
  fetchEntitiesByIds,
  mapEntitiesToDropdown} from "../../utils/paymentUtils";
import { SafeAreaView } from "react-native-safe-area-context";
import { fonts } from "../../styles/theme";
import AppText from "../../components/AppText";

interface PaymentPageProps {
  navigation: {
    navigate: (screen: string, params?: object) => void;
    goBack: () => void;
  };
}

const PaymentPage: React.FC<PaymentPageProps> = ({ navigation }) => {
  const [loading, setLoading] = useState(false);
  const [loadingDropdown, setLoadingDropdown] = useState(true);
  const [bkperEntities, setBkperEntities] = useState<
    { value: string; title: string }[]
  >([]);
  const [combinedInvestments, setCombinedInvestments] = useState<
    InvestmentModel[]
  >([]);
  const [entity, setEntity] = useState<string | null>(null);
  const [userData, setUserData] = useState<string>("");
  const [payment, setPayment] = useState<ShareholderTransactionListModel[]>([]);
  const [allPayments, setAllPayments] = useState<
    ShareholderTransactionListModel[]
  >([]);

  const [shareholderNameAPIOptions, setShareholderNameAPIOptions] = useState<
    ShareholderNameAPIOptionModel[]
  >([]);

  const apiUrl = Constants.expoConfig?.extra?.API_URL;

  // Fetch user info from AsyncStorage
  const fetchUserFromStorage = async () => {
    try {
      const apiResponse = await AsyncStorage.getItem("apiResponse");
      const user = JSON.parse(apiResponse || "{}");
      setUserData(user.fullName);
    } catch {
      setUserData("");
    }
  };

  useEffect(() => {
    fetchUserFromStorage();
  }, []);

  // Fetch investments when focused or refresh triggered
  useEffect(() => {
    setAuthToken();
    fetchInvestmentData();
  }, []);

  useEffect(() => {
    const handleRefresh = () => {
      fetchInvestmentData();
    };

    globalEventEmitter.on("globalRefresh", handleRefresh);
    return () => {
      globalEventEmitter.off("globalRefresh", handleRefresh);
    };
  }, []);

  // Fetch combined investment data
  const fetchInvestmentData = async () => {
    setLoading(true);
    try {
      const userIdToUse = await getUserIdToUse();
      const response = await ApiService.get<InvestmentModel>(
        `portal/partnerinvestment/${userIdToUse}`
      );

      console.log("the adfa s", response)
  
      const data = Array.isArray(response.data) ? response.data : [];

      setCombinedInvestments(data);

      const entityIdList = data.map((e: any) =>
        e.entityId.toString()
      );
      await loadBkperEntityDropdownList(entityIdList, data);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching investment data:", error);
      setLoading(false);
    }
  };

  const loadBkperEntityDropdownList = async (
    entityIdList: string[],
    investments: InvestmentModel[]
  ) => {
    setLoadingDropdown(true);
    try {
      const rowData = await fetchEntitiesByIds(entityIdList, apiUrl);
      if (rowData.length === 0) return;

      console.log("rowDat>>>>>>>.a", rowData);
      const mappedData = mapEntitiesToDropdown(rowData);
      console.log("mappedData", mappedData);
      setBkperEntities(mappedData);

      if (!entity && mappedData.length > 0) setEntity(mappedData[0].value);

      const shareholderOptions = buildShareholderOptions(
        rowData,
        investments,
      );
      console.log("shareholderOptions", shareholderOptions);
      setShareholderNameAPIOptions(shareholderOptions);

      const selectedBookId = mappedData[0].value;
      await loadPaymentsForBookId(selectedBookId, shareholderOptions);
    } catch (error) {
      console.error("Error fetching entity dropdown:", error);
    } finally {
      setLoadingDropdown(false);
    }
  };

  const loadPaymentsForBookId = async (
    bookId: string,
    options: ShareholderNameAPIOptionModel[],
  ) => {
    const aggregated = aggregateShareholderOptions(
      options,
      bookId,
    );
    if (aggregated) await fetchPaymentData(aggregated);
  };
  // Fetch payment data
  const fetchPaymentData = async (data: SelectedShareholderNameAPIOptions) => {
    setLoading(true);
    try {
      const response = await ApiService.post<any>(
        `bkper/transaction/getShareholderTransactionList`,
        data,
        { baseURL: apiUrl }
      );
      const rawData = response.data || [];
      console.log("rawData", rawData);
      setPayment(rawData);
      setAllPayments(rawData);
    } catch (error) {
      console.error("Error fetching payments:", error);
    } finally {
      setLoading(false);
    }
  };

  const onEntityDropdownChange = async (value: string) => {
    setEntity(value);

    await loadPaymentsForBookId(value, shareholderNameAPIOptions);
  };
  
  const renderPaymentItem = ({
    item}: {
    item: ShareholderTransactionListModel;
  }) => (
    <TouchableOpacity
      onPress={() =>
        navigation.navigate("PaymentDetails", {
          payment: item,
          sourcePage: "PaymentDetails"})
      }
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between"},
        commonStyles.subContainer,
        commonStyles.p14,
        commonStyles.mt20,
        commonStyles.mlar2,
      ]}
    >
      <View>
        <AppText
          testID={`type-${item.transaction.id}`}
          style={[commonStyles.detailsLabel, { fontSize: 16 }]}
        >
          {inferShareholderTransactionType(item.transaction)}
        </AppText>
        <AppText
          testID={`date-${item.transaction.id}`}
          style={[commonStyles.detailsLabel, { fontSize: 16 }]}
        >
          {formatDate(item.transaction.date)}
        </AppText>
      </View>

      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <AppText
          testID={`amount-${item.transaction.id}`}
          style={{
            fontSize: 28,
            //fontWeight: "400",
            color: "#3228D9",
            marginRight: 8,
            fontFamily: fonts.medium
          }}
        >
          $
          {Number(item.transaction.amount).toLocaleString(undefined, {
            maximumFractionDigits: 0})}
        </AppText>
        <AntDesign
          testID={`arrow-${item.transaction.id}`}
          name="arrow-right"
          size={20}
          color="#666"
        />
      </View>
    </TouchableOpacity>
  );

  return (
    <SafeAreaView edges={[ "bottom", "left", "right"]} style={[commonStyles.container, commonStyles.screenBottomGap]}>
      <View style={commonStyles.headerContainer}>
        <AppText testID="payment" style={commonStyles.header}>
          Your Payments
        </AppText>
      </View>

      {loadingDropdown ? (
        <View
          testID="skeleton-loader"
          style={{ width: "100%", paddingHorizontal: 20, height: 55 }}
        >
          <SkeletonLoader rows={1} />
        </View>
      ) : (
        <>
          <View
            testID="entity-dropdown"
            style={{ width: "100%", paddingHorizontal: 20, height: 55 }}
          >
            <CommonDropdown
              data={bkperEntities}
              value={entity}
              onChange={(item) => onEntityDropdownChange(item.value)}
              placeholder="Select Entity"
            />
          </View>
          {/* <View
            style={{
              flexDirection: "row",
              width: "100%",
              paddingHorizontal: 20,
              height: 55,
              alignItems: "center",
              justifyContent: "space-between"}}
          > */}
            {/* <View testID="year-dropdown" style={{ flex: 2, marginRight: 10 }}>
              <CommonDropdown
                data={YearStaticData}
                value={year}
                onChange={(item) => onYearDropdownChange(item.value)}
                placeholder="Year"
              />
            </View> */}
            {/* <View testID="quarter-dropdown" style={{ flex: 1 }}>
              <CommonDropdown
                data={QuarterStaticData}
                testID="quarter-dropdown-data"
                value={quarter}
                onChange={(item: { value: string }) =>
                  onQuarterDropdownChange(item.value)
                }
                placeholder="Quarter"
              />
            </View> */}
          {/* </View> */}
        </>
      )}

      <ScrollView
        contentContainerStyle={PropertiesStyle.scrollContainer}
        testID="scrollView"
      >
        <AppText testID="details" style={[commonStyles.detailsHeader]}>
          Details
        </AppText>

        {loading ? (
          <SkeletonLoader rows={10} />
        ) : !loadingDropdown && (!payment || payment.length === 0) ? (
          <AppText testID="emptyProperty" style={commonStyles.emptyData}>
            No Payments available
          </AppText>
        ) : (
          <FlatList
            data={payment}
            renderItem={renderPaymentItem}
            contentContainerStyle={commonStyles.pb16}
            scrollEnabled={Platform.OS === "web"}
          />
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

export default PaymentPage;
